<cfp-card
  [title]="'building.summary.measures' | translate"
  [useH1]="useH1()"
  [dataTest]="'businessCaseBuilding'"
  [id]="'businessCase'"
>
  <cfp-info-modal-button
    afterTitle
    class="ms-2"
    [title]="'info.business-case.header'"
    [body]="'info.business-case.body'"
  />

  @let showM2Value = showM2();
  @if (environment.components.savingsM2) {
    <div actions class="btn-group toggle-btn-group d-print-none">
      <cfp-button-toggle
        [buttonSize]="'small'"
        [id]="'buildingBusinessCaseShowM2'"
        liveRegionStatusUpdater
        [statusUpdateOnClick]="'liveAreaRegion.contentUpdatedBelow' | translate"
        [pressed]="showM2Value"
        (clicked)="toggleM2()"
        [dataTest]="'buildingShowPerAreaButton'"
        [ariaLabel]="'aria.label.toggleSquareUnit' | translate"
      >
        <cfp-measurement-area />
      </cfp-button-toggle>
    </div>
  }

  @let isLoadingValue = isLoading();
  @let hasErrorValue = hasError();
  <cfp-loading-spinner top [loading]="isLoadingValue" [error]="hasErrorValue" />

  @let businessCaseValue = businessCase();
  @if (businessCaseValue && !isLoadingValue && !hasErrorValue) {
    @if (hasSubsidyLicense()) {
      @defer {
        <cfp-subsidy-building-business-case-info [portfolioId]="portfolioId()" [buildingId]="buildingId()" />
      }
    }

    <cfp-info-list>
      <!-- Cost reduction -->
      <div
        cfpInfoListItem
        [label]="'businesscase.costreduction' | translate"
        [icon]="environment.icons.iconSavings"
        [dataTest]="showM2Value ? 'buildingCostReductionPerArea' : 'buildingCostReduction'"
      >
        @if (showM2Value) {
          <cfp-measurement-cost-per-square
            [value]="businessCaseValue.costReduction / businessCaseValue.area"
            [sourceCurrencyCode]="businessCaseValue.currencyCode"
          />
        } @else {
          {{
            businessCaseValue.costReduction
              | cfpCurrency: businessCaseValue.currencyCode : environment.currencyDisplay || 'symbol-narrow'
          }}
        }
      </div>
      <!-- /Cost reduction -->

      <!-- Investment -->
      <div
        cfpInfoListItem
        [label]="'businesscase.investment' | translate"
        [icon]="environment.icons.iconSubsidy"
        [dataTest]="'buildingInvestment' + (showM2Value ? 'PerArea' : '')"
      >
        @if (showM2Value) {
          <cfp-measurement-cost-per-square
            [value]="businessCaseValue.investment / businessCaseValue.area"
            [sourceCurrencyCode]="businessCaseValue.currencyCode"
          />
        } @else {
          {{
            businessCaseValue.investment
              | cfpCurrency: businessCaseValue.currencyCode : environment.currencyDisplay || 'symbol-narrow'
          }}
        }
      </div>
      <!-- /Investment -->

      <!-- Recovery Time -->
      <div
        cfpInfoListItem
        [label]="'businesscase.paybackPeriodYears' | translate"
        [icon]="environment.icons.iconReturnInvestment"
        [dataTest]="'buildingPaybackPeriodYears'"
      >
        <span
          [innerHtml]="
            businessCaseValue.paybackPeriodYears
              | paybackPeriod: businessCaseValue.investment : businessCaseValue.costReduction
          "
        ></span>
      </div>
      <!-- /Recovery Time -->

      <!-- Co2 reduction -->
      <div
        cfpInfoListItem
        [label]="'businesscase.co2ConsumptionReduction' | translate"
        [icon]="environment.icons.iconCo2Reduction"
        [dataTest]="
          environment.components.co2ConsumptionReductionPercentage
            ? 'buildingco2ConsumptionReductionPercentage'
            : showM2Value
              ? 'buildingCo2ConsumptionReductionPerArea'
              : 'buildingCo2ConsumptionReduction'
        "
      >
        @if (businessCaseValue.co2ConsumptionReductionPercentage === 0) {
          -
        } @else {
          @if (environment.components.co2ConsumptionReductionPercentage) {
            <span [innerHtml]="businessCaseValue.co2ConsumptionReductionPercentage | number: '1.0-0'"></span>%
          } @else {
            @if (showM2Value) {
              {{ businessCaseValue.co2ConsumptionReductionPercentage | number: '1.0-0' }}%
              (<cfp-measurement-mass-per-square
                [value]="businessCaseValue.co2ConsumptionReduction / businessCaseValue.area"
                [addCo2]="false"
              />)
            } @else {
              {{ businessCaseValue.co2ConsumptionReductionPercentage | number: '1.0-0' }}% (<span
                data-test="buildingCo2ConsumptionReductionInTons"
                [innerHtml]="businessCaseValue.co2ConsumptionReduction | co2: false"
              ></span
              >)
            }
          }
        }
      </div>
      <!-- /Co2 reduction -->

      <!-- Energy usage reduction -->
      @let businessCaseEnergyUsageReductionInfoListRowComponentValue =
        businessCaseEnergyUsageReductionInfoListRowComponent();
      @if (businessCaseEnergyUsageReductionInfoListRowComponentValue) {
        <ng-container *ngTemplateOutlet="businessCaseEnergyUsageReductionInfoListRowComponentValue.template()" />
      }
      <!-- /Energy usage reduction -->
    </cfp-info-list>

    @if (
      showEnergyUsage() &&
      environment.components.showBuildingBusinessCaseEnergyConsumptionReduction !== false &&
      environment.components.showEnergyUsageInBusinesscase !== false &&
      (businessCaseValue.electricityConsumptionReductionInKwh ||
        businessCaseValue.gasConsumptionReductionInM3 ||
        businessCaseValue.heatConsumptionReductionInGJ)
    ) {
      <cfp-business-case-energy-usage-reduction-info-list-row
        [building]="building()"
        [electricityConsumptionReductionInKwh]="businessCaseValue.electricityConsumptionReductionInKwh"
        [electricityConsumptionReductionInKwhM2]="businessCaseValue.electricityConsumptionReductionInKwhM2"
        [gasConsumptionReductionInM3]="businessCaseValue.gasConsumptionReductionInM3"
        [gasConsumptionReductionInM3M2]="businessCaseValue.gasConsumptionReductionInM3M2"
        [heatConsumptionReductionInGJ]="businessCaseValue.heatConsumptionReductionInGJ"
        [heatConsumptionReductionInGJM2]="businessCaseValue.heatConsumptionReductionInGJM2"
        [oilConsumptionReductionInLiter]="businessCaseValue.oilConsumptionReductionInLiter"
        [oilConsumptionReductionInLiterM2]="businessCaseValue.oilConsumptionReductionInLiterM2"
        [coalConsumptionReductionInTons]="businessCaseValue.coalConsumptionReductionInTons"
        [coalConsumptionReductionInTonsM2]="businessCaseValue.coalConsumptionReductionInTonsM2"
        [woodConsumptionReductionInM3]="businessCaseValue.woodConsumptionReductionInM3"
        [woodConsumptionReductionInM3M2]="businessCaseValue.woodConsumptionReductionInM3M2"
        [pelletConsumptionReductionInTons]="businessCaseValue.pelletConsumptionReductionInTons"
        [pelletConsumptionReductionInTonsM2]="businessCaseValue.pelletConsumptionReductionInTonsM2"
        [showM2]="showM2Value"
      />
    }
  }
</cfp-card>
